// Design System Button Component
// Unified button component with all variants and features

import React, { useState, forwardRef } from 'react';
import { Button as MuiButton, CircularProgress, styled } from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens, shadowTokens, transitionTokens } from '../../tokens';

// Fallback colors if tokens aren't loaded
const fallbackGoldColor = '#FFD859';

// Styled Button with design system tokens
const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => !['loading', 'magical', 'floating'].includes(prop),
})(({ theme, size = 'medium', loading, magical, floating }) => {
  const mode = theme.palette.mode;
  
  // Size configurations
  const sizeConfig = {
    small: {
      padding: `${spacingTokens.component.xs} ${spacingTokens.component.md}`,
      fontSize: '0.875rem',
      height: 36,
    },
    medium: {
      padding: `${spacingTokens.component.sm} ${spacingTokens.component.lg}`,
      fontSize: '1rem',
      height: 42,
    },
    large: {
      padding: `${spacingTokens.component.md} ${spacingTokens.component.xl}`,
      fontSize: '1.125rem',
      height: 48,
    },
  };

  return {
    position: 'relative',
    borderRadius: spacingTokens.component.lg,
    transition: transitionTokens.standard,
    textTransform: 'none',
    fontWeight: 500,
    ...sizeConfig[size],
    
    // Loading state
    ...(loading && {
      pointerEvents: 'none',
      opacity: 0.7,
    }),
    
    // Magical effect (glow animation)
    ...(magical && {
      animation: 'glow 2s ease-in-out infinite',
      '@keyframes glow': {
        '0%': {
          boxShadow: `0 0 5px ${colorTokens?.base?.gold?.[400] || fallbackGoldColor}40, 0 0 10px ${colorTokens?.base?.gold?.[400] || fallbackGoldColor}1A`,
        },
        '50%': {
          boxShadow: `0 0 10px ${colorTokens?.base?.gold?.[400] || fallbackGoldColor}66, 0 0 20px ${colorTokens?.base?.gold?.[400] || fallbackGoldColor}33`,
        },
        '100%': {
          boxShadow: `0 0 5px ${colorTokens?.base?.gold?.[400] || fallbackGoldColor}40, 0 0 10px ${colorTokens?.base?.gold?.[400] || fallbackGoldColor}1A`,
        },
      },
    }),
    
    // Floating effect
    ...(floating && {
      animation: 'float 3s ease-in-out infinite',
      '@keyframes float': {
        '0%, 100%': {
          transform: 'translateY(0)',
        },
        '50%': {
          transform: 'translateY(-5px)',
        },
      },
    }),
    
    // Hover effects - reduced distance to prevent flickering
    '&:hover': {
      transform: loading ? 'none' : 'translateY(-1px)',
      transition: 'transform 0.15s ease-out, box-shadow 0.15s ease-out',
    },
    
    '&:active': {
      transform: loading ? 'none' : 'translateY(1px)',
      transition: 'transform 0.1s ease-out',
    },
    
    // Variant styles
    '&.MuiButton-containedPrimary': {
      background: mode === 'dark'
        ? `linear-gradient(135deg, ${colorTokens.dark.primary.dark} 0%, ${colorTokens.dark.primary.main} 100%)`
        : `linear-gradient(135deg, ${colorTokens.light.primary.dark} 0%, ${colorTokens.light.primary.main} 100%)`,
      boxShadow: shadowTokens?.semantic?.component?.button?.[mode] || shadowTokens?.elevations?.[2]?.[mode] || '0 2px 4px rgba(0, 0, 0, 0.1)',
      '&:hover': {
        background: mode === 'dark'
          ? `linear-gradient(135deg, ${colorTokens.dark.primary.main} 0%, ${colorTokens.dark.primary.light} 100%)`
          : `linear-gradient(135deg, ${colorTokens.light.primary.main} 0%, ${colorTokens.light.primary.light} 100%)`,
        boxShadow: loading ? (shadowTokens?.semantic?.component?.button?.[mode] || '0 2px 4px rgba(0, 0, 0, 0.1)') : (shadowTokens?.semantic?.component?.buttonHover?.[mode] || '0 4px 8px rgba(0, 0, 0, 0.15)'),
      },
    },
    
    '&.MuiButton-containedSecondary': {
      background: mode === 'dark'
        ? `linear-gradient(135deg, ${colorTokens.dark.secondary.dark} 0%, ${colorTokens.dark.secondary.main} 100%)`
        : `linear-gradient(135deg, ${colorTokens.light.secondary.dark} 0%, ${colorTokens.light.secondary.main} 100%)`,
      boxShadow: shadowTokens?.semantic?.component?.button?.[mode] || shadowTokens?.elevations?.[2]?.[mode] || '0 2px 4px rgba(0, 0, 0, 0.1)',
      color: mode === 'dark' ? colorTokens.dark.secondary.contrastText : colorTokens.light.secondary.contrastText,
      '&:hover': {
        background: mode === 'dark'
          ? `linear-gradient(135deg, ${colorTokens.dark.secondary.main} 0%, ${colorTokens.dark.secondary.light} 100%)`
          : `linear-gradient(135deg, ${colorTokens.light.secondary.main} 0%, ${colorTokens.light.secondary.light} 100%)`,
        boxShadow: loading ? (shadowTokens?.semantic?.component?.button?.[mode] || '0 2px 4px rgba(0, 0, 0, 0.1)') : (shadowTokens?.semantic?.component?.buttonHover?.[mode] || '0 4px 8px rgba(0, 0, 0, 0.15)'),
      },
    },
    
    '&.MuiButton-outlined': {
      borderWidth: 2,
      '&:hover': {
        borderWidth: 2,
        transform: loading ? 'none' : 'translateY(-2px) !important',
      },
    },
    
    '&.MuiButton-text': {
      '&:hover': {
        backgroundColor: mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.08)'
          : 'rgba(0, 0, 0, 0.04)',
      },
    },
    
    // Shine effect removed - was causing hover flickering issues
    
    // Disabled state
    '&.Mui-disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  };
});

// Loading component
const LoadingIndicator = ({ size = 20, sx = {} }) => (
  <CircularProgress
    size={size}
    color="inherit"
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -size / 2,
      marginLeft: -size / 2,
      ...sx,
    }}
  />
);


/**
 * Button - Unified button component for the design system
 * 
 * Features:
 * - All MUI Button props supported
 * - Loading state with spinner
 * - Magical glow effect
 * - Floating animation
 * - Design system tokens integration
 * - Consistent hover/active states
 * 
 * @example
 * // Basic usage
 * <Button variant="contained" color="primary">Click me</Button>
 * 
 * // With loading
 * <Button loading>Saving...</Button>
 * 
 * // With magical effect
 * <Button magical variant="contained" color="secondary">Magic!</Button>
 */
const Button = forwardRef(({
  children,
  loading = false,
  loadingPosition = 'center',
  magical = false,
  floating = false,
  disabled,
  startIcon,
  endIcon,
  onClick,
  ...props
}, ref) => {
  const [isClicked, setIsClicked] = useState(false);
  
  // Handle click with effect
  const handleClick = (event) => {
    if (!disabled && !loading) {
      setIsClicked(true);
      setTimeout(() => setIsClicked(false), 300);
      onClick?.(event);
    }
  };
  
  // Show/hide children when loading
  const showChildren = loading && loadingPosition === 'center' 
    ? { visibility: 'hidden' } 
    : {};
  
  return (
    <StyledButton
      ref={ref}
      loading={loading}
      magical={magical}
      floating={floating}
      disabled={disabled || loading}
      startIcon={loading && loadingPosition === 'start' ? <CircularProgress size={16} color="inherit" /> : startIcon}
      endIcon={loading && loadingPosition === 'end' ? <CircularProgress size={16} color="inherit" /> : endIcon}
      onClick={handleClick}
      {...props}
    >
      {loading && loadingPosition === 'center' && <LoadingIndicator />}
      <span style={showChildren}>{children}</span>
    </StyledButton>
  );
});

Button.propTypes = {
  children: PropTypes.node.isRequired,
  loading: PropTypes.bool,
  loadingPosition: PropTypes.oneOf(['center', 'start', 'end']),
  magical: PropTypes.bool,
  floating: PropTypes.bool,
  disabled: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  onClick: PropTypes.func,
  // All other MUI Button props are supported
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'info', 'warning', 'inherit']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fullWidth: PropTypes.bool,
};

Button.displayName = 'Button';

export default Button;